import { ArrowLeft, ArrowRight, Plus, Trash2 } from "lucide-react";

const Label = ({ children }) => (
  <label className="block text-xs uppercase tracking-[0.15em] font-bold text-[#71717A] mb-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</label>
);
const Input = ({ testid, ...props }) => <input data-testid={testid} className="brutalist-input" {...props} />;
const SectionTitle = ({ children }) => (
  <h3 className="text-sm font-bold text-[#00A3E0] uppercase tracking-[0.15em] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{children}</h3>
);

const WIRING_CODES = [
  { code: "A", desc: "Thermoplastic insulated/sheathed" },
  { code: "B", desc: "Thermoplastic in metallic conduit" },
  { code: "C", desc: "Thermoplastic in non-metallic conduit" },
  { code: "D", desc: "Thermoplastic in metallic trunking" },
  { code: "E", desc: "Thermoplastic in non-metallic trunking" },
  { code: "F", desc: "Thermoplastic SWA cables" },
  { code: "G", desc: "Thermosetting SWA cables" },
];

const EMPTY_CIRCUIT = {
  ref: "", designation: "", csa_live: "", csa_cpc: "", wiring_type: "A", points: "",
  ocpd_type: "", ocpd_rating: "", ocpd_breaking: "", ocpd_max_disconnect: "",
  rcd_type: "", rcd_idn: "", rcd_rating: "",
  r1r2: "", r2: "", ring_r1: "", ring_rn: "", ring_r2: "",
  ir_voltage: "500", ir_live_live: "", ir_live_earth: "",
  zs: "", max_zs: "", polarity: "", rcd_time: "", rcd_test_btn: "", afdd: "",
};

export default function DomesticEICStep4({ formData, updateForm, onNext, onPrev }) {
  const set = (field) => (e) => updateForm({ [field]: e.target.value });
  const circuits = formData.circuits || [{ ...EMPTY_CIRCUIT, ref: "1" }];

  const updateCircuit = (idx, field, value) => {
    const updated = circuits.map((c, i) => (i === idx ? { ...c, [field]: value } : c));
    updateForm({ circuits: updated });
  };

  const addCircuit = () => {
    const nextRef = String(circuits.length + 1);
    updateForm({ circuits: [...circuits, { ...EMPTY_CIRCUIT, ref: nextRef }] });
  };

  const removeCircuit = (idx) => {
    if (circuits.length <= 1) return;
    updateForm({ circuits: circuits.filter((_, i) => i !== idx) });
  };

  return (
    <div className="form-container max-w-5xl mx-auto" data-testid="eic-step-4">
      <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
        Test Instruments & Circuit Details
      </h2>

      {/* Wiring Codes Reference */}
      <div className="border-b-2 border-[#E4E4E7] pb-4 mb-5">
        <SectionTitle>Codes for Types of Wiring</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-1">
          {WIRING_CODES.map((w) => (
            <div key={w.code} className="text-xs py-1">
              <span className="font-bold text-[#0A0A0A]">{w.code}</span>
              <span className="text-[#71717A]"> — {w.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Test Instruments */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <SectionTitle>Test Instrument(s) Used</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="form-field"><Label>Earth fault loop impedance</Label><Input testid="eic-inst-earth" value={formData.instrument_earth_loop} onChange={set("instrument_earth_loop")} placeholder="N/A" /></div>
          <div className="form-field"><Label>Insulation resistance</Label><Input testid="eic-inst-insulation" value={formData.instrument_insulation} onChange={set("instrument_insulation")} placeholder="N/A" /></div>
          <div className="form-field"><Label>Continuity</Label><Input testid="eic-inst-continuity" value={formData.instrument_continuity} onChange={set("instrument_continuity")} placeholder="N/A" /></div>
          <div className="form-field"><Label>RCD</Label><Input testid="eic-inst-rcd" value={formData.instrument_rcd} onChange={set("instrument_rcd")} placeholder="N/A" /></div>
          <div className="form-field"><Label>MFT</Label><Input testid="eic-inst-mft" value={formData.instrument_mft} onChange={set("instrument_mft")} placeholder="N/A" /></div>
          <div className="form-field"><Label>Other</Label><Input testid="eic-inst-other" value={formData.instrument_other} onChange={set("instrument_other")} placeholder="N/A" /></div>
        </div>
        <div className="mt-3">
          <Label>Circuits vulnerable to damage during testing / remarks</Label>
          <textarea data-testid="eic-vulnerable" className="brutalist-input min-h-[60px] resize-y"
            value={formData.vulnerable_circuits} onChange={set("vulnerable_circuits")} placeholder="N/A" />
        </div>
      </div>

      {/* Distribution Board Details */}
      <div className="border-b-2 border-[#E4E4E7] pb-5 mb-5">
        <SectionTitle>Distribution Board Details</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="form-field"><Label>DB ref</Label><Input testid="eic-db-ref" value={formData.db_ref} onChange={set("db_ref")} /></div>
          <div className="md:col-span-3"><Label>DB location</Label><Input testid="eic-db-location" value={formData.db_location} onChange={set("db_location")} placeholder="e.g. Hallway" /></div>
          <div className="form-field"><Label>Zs at board (Ohm)</Label><Input testid="eic-db-ze" value={formData.db_ze} onChange={set("db_ze")} /></div>
          <div className="form-field"><Label>Ipf at board (kA)</Label><Input testid="eic-db-ipf" value={formData.db_ipf} onChange={set("db_ipf")} /></div>
          <div className="form-field"><Label>Main switch type BSEN</Label><Input testid="eic-db-switch" value={formData.db_main_switch_type} onChange={set("db_main_switch_type")} /></div>
          <div className="form-field"><Label>Supplied from</Label><Input testid="eic-db-supplied" value={formData.db_supplied_from} onChange={set("db_supplied_from")} /></div>
          <div className="form-field"><Label>Rating (A)</Label><Input testid="eic-db-rating" value={formData.db_rating} onChange={set("db_rating")} /></div>
          <div className="form-field"><Label>No. of phases</Label><Input testid="eic-db-phases" value={formData.db_num_phases} onChange={set("db_num_phases")} /></div>
          <div className="form-field"><Label>SPD type / status</Label><Input testid="eic-db-spd" value={formData.db_spd} onChange={set("db_spd")} placeholder="N/A" /></div>
          <div className="form-field"><Label>Supply device BSEN ref</Label><Input testid="eic-db-supply-ref" value={formData.db_supply_device_ref} onChange={set("db_supply_device_ref")} /></div>
          <div className="form-field"><Label>Earth csa (mm²)</Label><Input testid="eic-db-earth-csa" value={formData.db_earth_csa} onChange={set("db_earth_csa")} /></div>
          <div className="form-field"><Label>Neutral csa (mm²)</Label><Input testid="eic-db-neutral-csa" value={formData.db_neutral_csa} onChange={set("db_neutral_csa")} /></div>
          <div className="form-field"><Label>Supply device rating (A)</Label><Input testid="eic-db-supply-rating" value={formData.db_supply_device_rating} onChange={set("db_supply_device_rating")} /></div>
        </div>
      </div>

      {/* Circuit Details Table */}
      <div className="mb-5">
        <div className="flex items-center justify-between mb-3">
          <SectionTitle>Circuit Details & Test Results</SectionTitle>
          <button type="button" onClick={addCircuit} data-testid="eic-add-circuit"
            className="brutalist-btn-outline !px-3 !py-1.5 flex items-center gap-1 text-xs">
            <Plus className="w-3 h-3" /> Add Circuit
          </button>
        </div>

        <div className="space-y-4" data-testid="eic-circuits-list">
          {circuits.map((circuit, idx) => (
            <div key={idx} className="border-2 border-[#E4E4E7] p-4 relative" data-testid={`eic-circuit-${idx}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Circuit {circuit.ref || idx + 1}
                </span>
                {circuits.length > 1 && (
                  <button type="button" onClick={() => removeCircuit(idx)} data-testid={`eic-remove-circuit-${idx}`}
                    className="text-red-500 hover:text-red-700 p-1">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Row 1: Basic circuit info */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-2">
                <div className="form-field"><Label>Ref</Label><input className="brutalist-input" value={circuit.ref} onChange={(e) => updateCircuit(idx, "ref", e.target.value)} /></div>
                <div className="col-span-2"><Label>Designation</Label><input className="brutalist-input" value={circuit.designation} onChange={(e) => updateCircuit(idx, "designation", e.target.value)} placeholder="e.g. Lighting" /></div>
                <div className="form-field"><Label>Live csa</Label><input className="brutalist-input" value={circuit.csa_live} onChange={(e) => updateCircuit(idx, "csa_live", e.target.value)} placeholder="mm²" /></div>
                <div className="form-field"><Label>CPC csa</Label><input className="brutalist-input" value={circuit.csa_cpc} onChange={(e) => updateCircuit(idx, "csa_cpc", e.target.value)} placeholder="mm²" /></div>
                <div className="form-field">
                  <Label>Wiring</Label>
                  <select className="brutalist-input" value={circuit.wiring_type} onChange={(e) => updateCircuit(idx, "wiring_type", e.target.value)}>
                    {WIRING_CODES.map((w) => <option key={w.code} value={w.code}>{w.code}</option>)}
                  </select>
                </div>
              </div>

              {/* Row 2: OCPD */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2">
                <div className="form-field"><Label>OCPD type</Label><input className="brutalist-input" value={circuit.ocpd_type} onChange={(e) => updateCircuit(idx, "ocpd_type", e.target.value)} placeholder="e.g. B" /></div>
                <div className="form-field"><Label>Rating (A)</Label><input className="brutalist-input" value={circuit.ocpd_rating} onChange={(e) => updateCircuit(idx, "ocpd_rating", e.target.value)} /></div>
                <div className="form-field"><Label>Breaking (kA)</Label><input className="brutalist-input" value={circuit.ocpd_breaking} onChange={(e) => updateCircuit(idx, "ocpd_breaking", e.target.value)} /></div>
                <div className="form-field"><Label>Max disc. (s)</Label><input className="brutalist-input" value={circuit.ocpd_max_disconnect} onChange={(e) => updateCircuit(idx, "ocpd_max_disconnect", e.target.value)} /></div>
                <div className="form-field"><Label>Points</Label><input className="brutalist-input" value={circuit.points} onChange={(e) => updateCircuit(idx, "points", e.target.value)} /></div>
              </div>

              {/* Row 3: RCD & Continuity */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-2">
                <div className="form-field"><Label>RCD type</Label><input className="brutalist-input" value={circuit.rcd_type} onChange={(e) => updateCircuit(idx, "rcd_type", e.target.value)} /></div>
                <div className="form-field"><Label>RCD IΔn (mA)</Label><input className="brutalist-input" value={circuit.rcd_idn} onChange={(e) => updateCircuit(idx, "rcd_idn", e.target.value)} /></div>
                <div className="form-field"><Label>R1+R2 (Ohm)</Label><input className="brutalist-input" value={circuit.r1r2} onChange={(e) => updateCircuit(idx, "r1r2", e.target.value)} /></div>
                <div className="form-field"><Label>R2 (Ohm)</Label><input className="brutalist-input" value={circuit.r2} onChange={(e) => updateCircuit(idx, "r2", e.target.value)} /></div>
                <div className="form-field"><Label>Zs (Ohm)</Label><input className="brutalist-input" value={circuit.zs} onChange={(e) => updateCircuit(idx, "zs", e.target.value)} /></div>
                <div className="form-field"><Label>Max Zs (Ohm)</Label><input className="brutalist-input" value={circuit.max_zs} onChange={(e) => updateCircuit(idx, "max_zs", e.target.value)} /></div>
              </div>

              {/* Row 4: Insulation Resistance & Polarity */}
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                <div className="form-field"><Label>IR voltage (V)</Label><input className="brutalist-input" value={circuit.ir_voltage} onChange={(e) => updateCircuit(idx, "ir_voltage", e.target.value)} /></div>
                <div className="form-field"><Label>IR L-L (MOhm)</Label><input className="brutalist-input" value={circuit.ir_live_live} onChange={(e) => updateCircuit(idx, "ir_live_live", e.target.value)} /></div>
                <div className="form-field"><Label>IR L-E (MOhm)</Label><input className="brutalist-input" value={circuit.ir_live_earth} onChange={(e) => updateCircuit(idx, "ir_live_earth", e.target.value)} /></div>
                <div className="form-field">
                  <Label>Polarity</Label>
                  <select className="brutalist-input" value={circuit.polarity} onChange={(e) => updateCircuit(idx, "polarity", e.target.value)}>
                    <option value="">-</option>
                    <option value="Pass">Pass</option>
                    <option value="Fail">Fail</option>
                  </select>
                </div>
                <div className="form-field"><Label>RCD time (ms)</Label><input className="brutalist-input" value={circuit.rcd_time} onChange={(e) => updateCircuit(idx, "rcd_time", e.target.value)} /></div>
                <div className="form-field">
                  <Label>RCD test btn</Label>
                  <select className="brutalist-input" value={circuit.rcd_test_btn} onChange={(e) => updateCircuit(idx, "rcd_test_btn", e.target.value)}>
                    <option value="">-</option>
                    <option value="Pass">Pass</option>
                    <option value="Fail">Fail</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-between">
        <button data-testid="eic-step-4-prev" onClick={onPrev} className="brutalist-btn-outline flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>
        <button data-testid="eic-step-4-next" onClick={onNext} className="brutalist-btn flex items-center gap-2">
          Next <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
